import { useRef, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useLocalization } from '@ui-framework/index';
import ButtonContainer from '../ButtonContainer';
import Form from './Form';

// onCancelSaving: optional; when given, Cancel stays active while saving and
// calls it (for example to abort an upload) instead of closing the dialog.
export default function FormDialog({
  title,
  onClose,
  onSubmit,
  onCancelSaving,
  ...formProps
}) {
  const formRef = useRef();
  const [saving, setSaving] = useState(false);
  const { formatMessage } = useLocalization();
  const close = () => {
    if (saving) return;
    if (!formRef.current?.isDirty()) {
      onClose();
      return;
    }
    const dialog = formRef.current.getActionDialog();
    dialog.init({
      title: formatMessage({
        id: 'UNSAVED_CHANGES',
        defaultMessage: 'Unsaved Changes',
      }),
      message: formatMessage({
        id: 'DISCARD_YOUR_UNSAVED_CHANGES',
        defaultMessage: 'Discard your unsaved changes?',
      }),
      buttons: [
        {
          label: formatMessage({
            id: 'CANCEL',
            defaultMessage: 'Cancel',
          }),
          onClick: () => dialog.close(),
        },
        {
          label: formatMessage({ id: 'DISCARD', defaultMessage: 'Discard' }),
          onClick: onClose,
        },
      ],
    });
    dialog.open();
  };
  return (
    <Dialog open onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Form
          {...formProps}
          ref={formRef}
          dialog
          onSubmit={async values => {
            setSaving(true);
            try {
              return await onSubmit(values);
            } finally {
              setSaving(false);
            }
          }}
        />
        <ButtonContainer>
          <Button
            disabled={saving && !onCancelSaving}
            onClick={saving ? onCancelSaving : close}>
            {formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() =>
              formRef.current?.submit({ formId: formProps.formId })
            }>
            {formatMessage({ id: 'SAVE', defaultMessage: 'Save' })}
          </Button>
        </ButtonContainer>
      </DialogContent>
    </Dialog>
  );
}
